// R70 — THE SPELLBOOK SAID SHE HAD NO SPELLS WHEN IT COULD NOT ASK.
//
// **THE FIFTH ROUTE FOUND ANSWERING A REFUSAL AS AN EMPTY COLLECTION**, after R55's
// `handleVideoProgress`, R60's `handleRoomBackgrounds` and `handleLalaMessages` and R64's
// `handleCauldronList`. `handleSpellsBrowse` caught its own database error and answered
//
//     200 { ok: true, categories: [], spells: [] }
//
// and the client's guard was `if (!d?.ok) return;`. So — R64's finding word for word — the `ok: true`
// makes it WORSE than silence rather than equal to it: a failed read did not fail to update the
// book, **it OVERWROTE the index with an empty one** and the contents page printed
//
//     "No spells in this category yet."
//
// That is a sentence about the CATALOG, printed over a book that never arrived (R60's Card Library
// finding, R64's Cauldron finding, a third time). The Conjure Spellbook lists the curated spells AND
// **every spell she has ever had written for her**, so on a bad read the room tells her the workings
// she paid generations for are not there.
//
// **AND THIS ONE FORGES A SECOND THING, WHICH IS WHY IT IS THE SHARPEST OF THE FIVE: THE LOCK.**
// T8-2's own comment in App.tsx says *"The client never decides this — it is told."* The forged
// payload carries no `locked`, so `Boolean(d.locked)` is FALSE and the client was told the Magic
// Room is OPEN by a catch block. That is R20's finding exactly — the client failing OPEN where the
// server fails closed. It is inert TODAY only because the same forged payload leaves nothing on the
// shelf to tap, which is not a property anybody designed and not one to rely on.
//
// **ONE READ, ALL FOUR FIELDS, SO THE SECOND COPY CANNOT DRIFT (R31/R60).** The room read this route
// TWICE — once on mount and once after a generation — and the second copy read `spells` and dropped
// `locked`, `categories` and the lock payload. `attachToMessages` was the same finding in R60: two
// hand-typed copies of one read, already drifted. There is one now.
//
// **THREE ANSWERS, NOT FOUR, AND THAT IS ARGUED RATHER THAN COPIED (R58's ruling).** The pot's
// `CauldronAnswer` carries `sign-in`, because the Cauldron is behind a session. This route is OPEN:
// it reads the session to decide `locked` and refuses NOBODY, and 401 is not an answer it can give.
// **A refusal branch for a refusal a route cannot make is a second answer key.**
//
// `doFetch` is a PARAMETER (R46), so every answer the route can give is reachable with no browser,
// no session and no database.
export type SpellBrowseAnswer<T> = {
  ok: boolean;
  kind: "ok" | "refused" | "offline";
  spells: T[];
  categories: string[];
  // WHAT THE SERVER SAID ABOUT THE DOOR, never what the client guessed. `null` is "it did not say",
  // which is a THIRD fact beside locked and unlocked (R18) and is what a refusal answers with.
  locked: boolean | null;
  lock: unknown | null;
  message: string;
};

export const spellBrowsePath = "/api/spells";

export function spellBrowseUrl(category: string) {
  return category ? `${spellBrowsePath}?category=${encodeURIComponent(category)}` : spellBrowsePath;
}

const refusal = <T,>(kind: "refused" | "offline", message = ""): SpellBrowseAnswer<T> => ({
  ok: false, kind, spells: [], categories: [], locked: null, lock: null, message,
});

export async function listSpells<T>(
  category: string,
  doFetch: typeof fetch = typeof fetch === "function" ? fetch : (undefined as never),
): Promise<SpellBrowseAnswer<T>> {
  let response: Response;
  try {
    response = await doFetch(spellBrowseUrl(category));
  } catch {
    // R18/R63: a request that never landed is TRANSPORT NOISE, not the server's word about her book.
    return refusal<T>("offline");
  }
  const data = await response.json().catch(() => null) as
    | { ok?: boolean; spells?: unknown; categories?: unknown; locked?: unknown; message?: string }
    | null;
  // **THE GUARD IS THE SHAPE, NOT THE STATUS CODE** (R47/R53/R55): this route answers a refusal at
  // HTTP 200 with `{ ok: false }`, as six other routes in this house already do. A 200 carrying no
  // `spells` ARRAY is a refusal too — that is what the forged payload used to look like from the
  // outside, and PRESENCE is the test, never truthiness (R53).
  if (!response.ok || !data || data.ok === false || !Array.isArray(data.spells)) {
    return refusal<T>("refused", typeof data?.message === "string" ? data.message : "");
  }
  return {
    ok: true,
    kind: "ok",
    spells: data.spells as T[],
    categories: Array.isArray(data.categories) ? (data.categories as string[]) : [],
    locked: Boolean(data.locked),
    // T8-3's box is fed by the SERVER's own lock payload. It rides only on a locked answer, so an
    // open one cannot leave a stale box behind it.
    lock: data.locked ? data : null,
    message: "",
  };
}

// A read that landed says NOTHING — the index appearing IS the answer (R51..R66 on this question).
// A refusal always says something, in the room's own words, because this route composes no sentence
// of its own on the path that matters.
export function spellBrowseNote<T>(answer: SpellBrowseAnswer<T>, failed: string) {
  if (answer.ok) return "";
  return answer.message || failed;
}
